<template>
  <div :class="rootClass">
    <div
      v-if="thumbnailPath"
      class="thumbnail-container content"
    >
      <div
        v-if="isLoading"
        class="spinner-container"
      >
        <b-spinner
          variant="secondary"
          type="grow"
          label="Spinning"
        />
      </div>
      <div
        v-show="!isLoading"
        class="links-wrapper"
      >
        <a
          v-b-tooltip.hover.viewport.noninteractive="'Download'"
          :href="getDownloadUrl(item)"
          :download="getDownloadName(item)"
          @click.prevent.stop="downloadItem(item)"
        >
          <b-icon icon="download" />
        </a>
        <a
          v-b-tooltip.hover.viewport.noninteractive="'View'"
          href="#"
          @click.prevent.stop="openViewer"
        >
          <b-icon icon="zoom-in" />
        </a>
      </div>
      <img
        v-show="!isLoading"
        :src="thumbnailPath"
        alt="product-thumbnail"
        onError="this.src='/images/image.svg'"
        @load="onLoad"
      >
    </div>
    <div
      v-if="!thumbnailPath"
      class="d-flex justify-content-center align-items-center w-100 h-100 content"
      v-html="drawFileSvg(item)"
    />
  </div>
</template>

<script>
import {drawFileSvg} from '@frontend/utils';
import {TYPE} from '@frontend/constants/file-image-types';
import 'viewerjs/dist/viewer.min.css'
import { api as viewerApi } from 'v-viewer'
import { mapGetters } from 'vuex'

export default {
  name: "ItemView",
  props: {
    item: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      isLoading: true
    }
  },
  computed: {
    ...mapGetters('fileBrowserProductSelector', [
      'items',
    ]),
    viewerImages () {
      return this.items
          .filter(item => item.type !== TYPE.FOLDER)
          .map(item => ({
            id: item.id,
            src: item.thumbnail_path || item.url || item.path,
            dataSource: item.full_url,
          }))
    },
    rootClass() {
      return this.item.type === TYPE.FOLDER ? 'folder' : 'image';
    },
    thumbnailPath() {
      if (this.item.name === 'product_placeholder_img') return this.item.full_url;
      return this.item.thumbnail_path || this.item.url || this.item.path;
    }
  },
  methods: {
    drawFileSvg: drawFileSvg,
    onLoad() {
      this.isLoading = false;
    },
    openViewer () {
      viewerApi({
        options: {
          url: 'dataSource',
          initialViewIndex: _.findIndex(this.viewerImages, ['id', this.item.id]),
        },
        images: this.viewerImages,
      })
    },
    async downloadItem(item) {
      const url = this.getDownloadUrl(item);
      const filename = this.getDownloadName(item);

      if (!url) {
        return;
      }

      try {
        const response = await fetch(url, { credentials: 'same-origin' });

        if (!response.ok) {
          throw new Error(`Download failed with status ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        const link = document.createElement('a');

        link.href = url;
        link.download = filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    getDownloadUrl(item) {
      const sourcePath = this.getLocalFilePath(item);

      if (sourcePath) {
        return `/share?file=${encodeURIComponent(sourcePath)}`;
      }

      return item.full_url || item.url || item.path || '';
    },
    getDownloadName(item) {
      const sourceParts = this.getFilenameParts(this.getSourcePath(item));
      const itemParts = this.getFilenameParts(item.name);
      const extension = this.isValidImageExtension(sourceParts.extension)
        ? sourceParts.extension
        : (this.isValidImageExtension(itemParts.extension) ? itemParts.extension : 'png');
      let baseName = itemParts.base || sourceParts.base || 'product_image';

      if (baseName === 'share' || baseName.startsWith('share_cropped_')) {
        baseName = sourceParts.base && sourceParts.base !== 'share' ? sourceParts.base : 'product_image';
      }

      return `${baseName}.${extension}`;
    },
    getLocalFilePath(item) {
      const sources = [
        item.original_path,
        item.full_url,
        item.url,
        item.path,
      ];

      for (const source of sources) {
        const path = this.extractLocalFilePath(source);

        if (path) {
          return path;
        }
      }

      return '';
    },
    extractLocalFilePath(value) {
      const source = String(value || '');

      if (!source) {
        return '';
      }

      try {
        const url = new URL(source, window.location.origin);
        const file = url.searchParams.get('file');

        if (file) {
          const localPath = this.safeDecode(file);
          return this.isStoragePath(localPath) ? localPath : '';
        }

        if (url.origin === window.location.origin && !source.startsWith('/share')) {
          const localPath = url.pathname.replace(/^\/+/, '');
          return this.isStoragePath(localPath) ? localPath : '';
        }

        return '';
      } catch (error) {
        const fileParam = source.match(/[?&]file=([^&]+)/);

        if (fileParam) {
          const localPath = this.safeDecode(fileParam[1]);
          return this.isStoragePath(localPath) ? localPath : '';
        }

        const localPath = source.split('?')[0].split('&')[0].replace(/^\/+/, '');
        return !source.includes('://') && this.isStoragePath(localPath) ? localPath : '';
      }
    },
    isStoragePath(path) {
      const cleanPath = String(path || '').replace(/\\/g, '/');

      return !cleanPath.includes('..')
        && /^(files|stock|uploads|outputs|user-folder|conversations|loaded_data|img)\//.test(cleanPath);
    },
    safeDecode(value) {
      try {
        return decodeURIComponent(String(value || ''));
      } catch (error) {
        return String(value || '');
      }
    },
    getSourcePath(item) {
      const source = this.getLocalFilePath(item) || item.full_url || item.url || item.path || item.name || '';

      try {
        const url = new URL(source, window.location.origin);
        return url.searchParams.get('file') || url.pathname;
      } catch (error) {
        const fileParam = String(source).match(/[?&]file=([^&]+)/);
        return fileParam ? fileParam[1] : source;
      }
    },
    getFilenameParts(value) {
      const filename = decodeURIComponent(String(value || ''))
        .split('?')[0]
        .split('&')[0]
        .split('/')
        .pop() || '';
      const dotIndex = filename.lastIndexOf('.');

      return {
        base: dotIndex > 0 ? filename.slice(0, dotIndex) : filename,
        extension: dotIndex > 0 ? filename.slice(dotIndex + 1).toLowerCase() : '',
      };
    },
    isValidImageExtension(extension) {
      return Boolean(extension)
        && !['undefined', 'null'].includes(extension)
        && /^[a-z0-9]{2,5}$/i.test(extension);
    }
  }
}
</script>

<style lang="scss" scoped>
.thumbnail-container {
  display: flex;

  img {
    flex-grow: 1;
    object-fit: contain;
  }
}

.c-grid-view-item__preview {
  .links-wrapper {
    display: none;
    position: absolute;
    top: .25rem;
    left: .5rem;

    :hover > & {
      display: flex;
      flex-direction: column;
    }
  }
}

.c-file-browser {
  .content {
    img, svg {
      max-width: 175px;
      max-height: 80px;
    }

    > svg {
      width: 30px;
    }

    .folder-icon-preview {
      width: 88px !important;
      height: 72.16px !important;
      max-width: 88px !important;
      max-height: 72.16px !important;
      flex-shrink: 0;

      svg {
        width: 100% !important;
        height: 100% !important;
        max-width: none !important;
        max-height: none !important;
      }
    }
  }

  .image {
    width: 100%;

    svg {
      width: auto;
    }
  }

  .spinner-container {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .folder {
    .content {
      width: 100% !important;
      height: 100px !important;
      background-color: rgb(240, 242, 244, 0.3);
    }
  }
}
</style>
